// PFMEA Coaching Tips Panel Component
// ============================================================
// Displays contextual coaching tips during PFMEA entry
// Uses STATIC tips - NO LLM TOKENS CONSUMED
// Only uses AI when user explicitly asks Magnus in Prime mode
// ============================================================

import React, { useState, useEffect } from 'react';
import {
    Lightbulb,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    Info,
    ChevronDown,
    ChevronUp,
    Sparkles,
    Settings,
    X,
    HelpCircle,
} from 'lucide-react';
import { PfmeaItem } from '../types';
import {
    CoachingTip,
    ValidationResult,
    UserPreferences,
    UserExperienceLevel,
    validatePfmeaItem,
    getFieldCoachingTips,
    getSxOGuidance,
    getSxOActions,
    getUserPreferences,
    saveUserPreferences,
} from '../utils/coachingTips';

// ============================================================
// COACHING TIP CARD
// ============================================================

interface TipCardProps {
    tip: CoachingTip;
    onDismiss?: () => void;
}

const TipCard: React.FC<TipCardProps> = ({ tip, onDismiss }) => {
    const getIcon = () => {
        switch (tip.severity) {
            case 'error': return <AlertCircle className="text-red-500" size={18} />;
            case 'warning': return <AlertTriangle className="text-amber-500" size={18} />;
            case 'success': return <CheckCircle className="text-green-500" size={18} />;
            case 'best-practice': return <Sparkles className="text-purple-500" size={18} />;
            default: return <Info className="text-blue-500" size={18} />;
        }
    };

    const getBgColor = () => {
        switch (tip.severity) {
            case 'error': return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
            case 'warning': return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
            case 'success': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
            case 'best-practice': return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
            default: return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
        }
    };

    return (
        <div className={`p-3 rounded-lg border ${getBgColor()} relative`}>
            {onDismiss && (
                <button
                    onClick={onDismiss}
                    className="absolute top-2 right-2 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
                >
                    <X size={14} className="text-gray-500" />
                </button>
            )}
            <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
                <div className="flex-1 pr-6">
                    <p className="font-medium text-sm text-[var(--text-primary)]">{tip.title}</p>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">{tip.message}</p>
                    {tip.aiagVdaRef && (
                        <p className="text-xs text-[var(--text-secondary)] mt-2 opacity-70">
                            📖 {tip.aiagVdaRef}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// S×O ACTION SUGGESTIONS
// ============================================================

interface SxOActionsProps {
    severity: number;
    occurrence: number;
}

const SxOActionsSuggestions: React.FC<SxOActionsProps> = ({ severity, occurrence }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const actions = getSxOActions(severity, occurrence);
    const guidance = getSxOGuidance(severity, occurrence);

    if (!guidance) return null;

    return (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border border-purple-200 dark:border-purple-800 p-3">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between"
            >
                <div className="flex items-center gap-2">
                    <Lightbulb className="text-yellow-500" size={18} />
                    <span className="font-medium text-sm">S×O Guidance: S={severity}, O={occurrence}</span>
                </div>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {isExpanded && (
                <div className="mt-3 space-y-2">
                    <TipCard tip={guidance} />

                    <div className="mt-3">
                        <p className="text-xs font-medium text-[var(--text-secondary)] mb-2">
                            💡 Recommended Actions (Static - No AI tokens):
                        </p>
                        <ul className="space-y-1">
                            {actions.map((action, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                                    <span className="text-purple-500">→</span>
                                    {action}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================
// VALIDATION PANEL
// ============================================================

interface ValidationPanelProps {
    validation: ValidationResult;
    onClose?: () => void;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({ validation, onClose: _onClose }) => {
    if (validation.isValid && validation.warnings.length === 0) {
        return (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                    <CheckCircle className="text-green-500" size={20} />
                    <span className="font-medium text-green-700 dark:text-green-400">
                        ✓ Ready for submission - All validations passed
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {validation.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertCircle className="text-red-500" size={20} />
                        <span className="font-medium text-red-700 dark:text-red-400">
                            {validation.errors.length} Error(s) - Must fix before submission
                        </span>
                    </div>
                    <div className="space-y-2">
                        {validation.errors.map(error => (
                            <TipCard key={error.id} tip={error} />
                        ))}
                    </div>
                </div>
            )}

            {validation.warnings.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="text-amber-500" size={20} />
                        <span className="font-medium text-amber-700 dark:text-amber-400">
                            {validation.warnings.length} Warning(s) - Review recommended
                        </span>
                    </div>
                    <div className="space-y-2">
                        {validation.warnings.map(warning => (
                            <TipCard key={warning.id} tip={warning} />
                        ))}
                    </div>
                </div>
            )}

            {validation.suggestions.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="text-purple-500" size={20} />
                        <span className="font-medium text-purple-700 dark:text-purple-400">
                            Best Practice Suggestions
                        </span>
                    </div>
                    <div className="space-y-2">
                        {validation.suggestions.map(suggestion => (
                            <TipCard key={suggestion.id} tip={suggestion} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================
// EXPERIENCE LEVEL SELECTOR
// ============================================================

interface ExperienceLevelSelectorProps {
    currentLevel: UserExperienceLevel;
    onChange: (level: UserExperienceLevel) => void;
}

const ExperienceLevelSelector: React.FC<ExperienceLevelSelectorProps> = ({ currentLevel, onChange }) => {
    const levels: { value: UserExperienceLevel; label: string; description: string }[] = [
        { value: 'beginner', label: 'Beginner', description: 'Show all coaching tips and guidance' },
        { value: 'intermediate', label: 'Intermediate', description: 'Show best practices and AIAG-VDA tips' },
        { value: 'expert', label: 'Expert', description: 'Minimal tips - only validation errors' },
    ];

    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">
                Experience Level (affects coaching detail)
            </label>
            <div className="grid grid-cols-3 gap-2">
                {levels.map(level => (
                    <button
                        key={level.value}
                        onClick={() => onChange(level.value)}
                        className={`p-3 rounded-lg border text-left transition-all ${currentLevel === level.value
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-[var(--border-color)] hover:border-primary-300'
                            }`}
                    >
                        <p className={`font-medium text-sm ${currentLevel === level.value ? 'text-primary-600' : 'text-[var(--text-primary)]'
                            }`}>
                            {level.label}
                        </p>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">{level.description}</p>
                    </button>
                ))}
            </div>
        </div>
    );
};

// ============================================================
// MAIN COACHING TIPS PANEL
// ============================================================

interface CoachingTipsPanelProps {
    item: PfmeaItem | Partial<PfmeaItem>;
    activeField?: string;
    showValidation?: boolean;
    onPreferencesChange?: (prefs: UserPreferences) => void;
}

export const CoachingTipsPanel: React.FC<CoachingTipsPanelProps> = ({
    item,
    activeField,
    showValidation = false,
    onPreferencesChange,
}) => {
    const [preferences, setPreferences] = useState<UserPreferences>(getUserPreferences());
    const [showSettings, setShowSettings] = useState(false);
    const [dismissedTips, setDismissedTips] = useState<Set<string>>(new Set());

    useEffect(() => {
        setPreferences(getUserPreferences());
    }, []);

    const handlePreferenceChange = (updates: Partial<UserPreferences>) => {
        const updated = { ...preferences, ...updates };
        saveUserPreferences(updated);
        setPreferences(updated);
        onPreferencesChange?.(updated);
    };

    const handleDismissTip = (tipId: string) => {
        setDismissedTips(prev => new Set([...prev, tipId]));
    };

    // Get validation result
    const validation = item.id ? validatePfmeaItem(item as PfmeaItem, preferences) : null;

    // Get field-specific tips
    const fieldTips = activeField
        ? getFieldCoachingTips(activeField, item, preferences).filter(t => !dismissedTips.has(t.id))
        : [];

    if (!preferences.showCoachingTips && !showValidation) {
        return null;
    }

    return (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
                <div className="flex items-center gap-2">
                    <Lightbulb className="text-yellow-500" size={20} />
                    <span className="font-medium text-[var(--text-primary)]">
                        Coaching Tips
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                        No AI tokens used
                    </span>
                </div>
                <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                >
                    <Settings size={16} className="text-[var(--text-secondary)]" />
                </button>
            </div>

            {/* Settings Panel */}
            {showSettings && (
                <div className="p-4 border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]">
                    <ExperienceLevelSelector
                        currentLevel={preferences.experienceLevel}
                        onChange={(level) => handlePreferenceChange({ experienceLevel: level })}
                    />
                    <div className="mt-4 space-y-2">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={preferences.showCoachingTips}
                                onChange={(e) => handlePreferenceChange({ showCoachingTips: e.target.checked })}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-[var(--text-primary)]">Show Coaching Tips</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={preferences.showValidationWarnings}
                                onChange={(e) => handlePreferenceChange({ showValidationWarnings: e.target.checked })}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-[var(--text-primary)]">Show Validation Warnings</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={preferences.showBestPractices}
                                onChange={(e) => handlePreferenceChange({ showBestPractices: e.target.checked })}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-[var(--text-primary)]">Show Best Practice Suggestions</span>
                        </label>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="p-4 space-y-4">
                {/* Field-specific tips */}
                {fieldTips.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">
                            Field: {activeField}
                        </p>
                        {fieldTips.map(tip => (
                            <TipCard
                                key={tip.id}
                                tip={tip}
                                onDismiss={() => handleDismissTip(tip.id)}
                            />
                        ))}
                    </div>
                )}

                {/* S×O Guidance (when both are set) */}
                {item.severity && item.occurrence && item.severity > 0 && item.occurrence > 0 && (
                    <SxOActionsSuggestions
                        severity={item.severity}
                        occurrence={item.occurrence}
                    />
                )}

                {/* Validation Panel */}
                {showValidation && validation && (
                    <div className="pt-4 border-t border-[var(--border-color)]">
                        <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-3">
                            Pre-Submission Validation
                        </p>
                        <ValidationPanel validation={validation} />
                    </div>
                )}

                {/* No tips message */}
                {fieldTips.length === 0 && !showValidation && !item.severity && (
                    <div className="text-center py-4 text-[var(--text-secondary)]">
                        <HelpCircle className="mx-auto mb-2 opacity-50" size={24} />
                        <p className="text-sm">Select a field to see coaching tips</p>
                        <p className="text-xs mt-1 opacity-70">
                            Tips are based on AIAG-VDA requirements - no AI tokens used
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoachingTipsPanel;
